import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, AlertCircle, ShieldCheck, ArrowLeft, Smartphone } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { authApi, type TotpEnrollment } from '../../lib/api'
import BrandLogo from '../../components/shared/BrandLogo'
import CodeInput from '../../components/shared/CodeInput'
import { tx } from '../../lib/i18n'
import LangSwitch from '../../components/shared/LangSwitch'

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
})

type FormData = z.infer<typeof schema>

type Step =
  | { kind: 'password' }
  | { kind: 'totp'; challengeToken: string }
  | { kind: 'totp-setup'; challengeToken: string; enrollment: TotpEnrollment | null }

const errorText = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
  (err instanceof Error ? err.message : fallback)

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const { login, completeLogin } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [step, setStep] = useState<Step>({ kind: 'password' })
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (data: FormData) => {
    setServerError(null)
    try {
      const next = await login(data.email, data.password)
      if (next.kind === 'done') {
        navigate('/dashboard', { replace: true })
      } else if (next.kind === 'totp') {
        setCode('')
        setStep({ kind: 'totp', challengeToken: next.challengeToken })
      } else {
        setCode('')
        setStep({ kind: 'totp-setup', challengeToken: next.challengeToken, enrollment: null })
        const res = await authApi.loginTwoFactorSetup(next.challengeToken)
        setStep({ kind: 'totp-setup', challengeToken: next.challengeToken, enrollment: res.data.data ?? null })
      }
    } catch (err: unknown) {
      setServerError(errorText(err, 'Login failed. Please try again.'))
    }
  }

  const submitCode = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (step.kind === 'password' || code.length !== 6) return
    setServerError(null)
    setBusy(true)
    try {
      const res =
        step.kind === 'totp'
          ? await authApi.loginTwoFactor(step.challengeToken, code)
          : await authApi.loginTwoFactorEnable(step.challengeToken, code)
      if (!res.data.data) throw new Error(res.data.error || 'Login failed')
      completeLogin(res.data.data)
      navigate('/dashboard', { replace: true })
    } catch (err) {
      setServerError(errorText(err, 'Invalid code'))
      setCode('')
      if ((err as { response?: { status?: number } })?.response?.status === 401) setStep({ kind: 'password' })
    } finally {
      setBusy(false)
    }
  }

  const back = () => {
    setStep({ kind: 'password' })
    setServerError(null)
    setCode('')
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-md">
        <div className="flex justify-end mb-3">
          <LangSwitch />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="flex flex-col items-center mb-8">
            <BrandLogo size={56} className="mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Queza Support</h1>
            <p className="mt-1 text-sm text-gray-500">คอนโซลสำหรับทีมดูแลระบบ</p>
          </div>

          {serverError && (
            <div className="mb-5 flex items-start gap-2.5 p-3 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{serverError}</p>
            </div>
          )}

          {step.kind === 'password' && (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email
                </label>
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  placeholder="you@queza.online"
                  className={`w-full px-4 py-2.5 text-sm border rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                    errors.email ? 'border-red-300' : 'border-gray-200'
                  }`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                  {tx('Password')}
                </label>
                <div className="relative">
                  <input
                    {...register('password')}
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••"
                    className={`w-full px-4 py-2.5 pr-10 text-sm border rounded-xl bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${
                      errors.password ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed shadow-sm shadow-indigo-600/25"
              >
                {isSubmitting && <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {isSubmitting ? tx('Signing in...') : tx('Sign In')}
              </button>
            </form>
          )}

          {step.kind === 'totp' && (
            <form onSubmit={submitCode} className="space-y-5">
              <div className="flex items-start gap-3 p-3 bg-indigo-50 rounded-xl">
                <ShieldCheck className="h-5 w-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  เปิดแอพ <b>Google Authenticator</b> แล้วกรอกรหัส 6 หลักของ <b>Queza Support</b>
                </p>
              </div>
              <CodeInput value={code} onChange={setCode} autoFocus disabled={busy} />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-60"
              >
                {busy ? tx('Verifying...') : tx('Verify')}
              </button>
              <button type="button" onClick={back} className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-4 w-4" /> ใช้บัญชีอื่น
              </button>
            </form>
          )}

          {step.kind === 'totp-setup' && (
            <form onSubmit={submitCode} className="space-y-4">
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                <Smartphone className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-700">
                  ระบบบังคับใช้การยืนยัน 2 ขั้นตอน — สแกน QR ด้วย <b>Google Authenticator</b> แล้วกรอกรหัส 6 หลัก
                </p>
              </div>
              {step.enrollment ? (
                <div className="flex flex-col items-center gap-2">
                  <img src={step.enrollment.qrDataUrl} alt="QR" className="w-48 h-48 rounded-lg border border-gray-100" />
                  <p className="text-[11px] text-gray-400 text-center break-all">
                    สแกนไม่ได้? กรอกรหัสนี้ในแอพ: <span className="font-mono text-gray-600">{step.enrollment.secret}</span>
                  </p>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-sm text-gray-400">{tx('Loading QR…')}</div>
              )}
              <CodeInput value={code} onChange={setCode} autoFocus disabled={busy} />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full py-2.5 px-4 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 disabled:opacity-60"
              >
                {busy ? tx('Verifying...') : tx('Turn on & sign in')}
              </button>
              <button type="button" onClick={back} className="w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                <ArrowLeft className="h-4 w-4" /> ยกเลิก
              </button>
            </form>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">Queza Support &copy; {new Date().getFullYear()}</p>
      </div>
    </div>
  )
}

export default LoginPage
